import { useEffect, useMemo, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import { Plugin, TextSelection } from "@tiptap/pm/state";
import { history, undo, redo } from "@tiptap/pm/history";

const StrictListDocument = Document.extend({
  content: "bulletList",
});

const EditorHistory = Extension.create({
  name: "editorHistory",
  addProseMirrorPlugins() {
    return [history()];
  },
  addKeyboardShortcuts() {
    const run = (command) =>
      this.editor.commands.command(({ state, dispatch }) => command(state, dispatch));

    return {
      "Mod-z": () => run(undo),
      "Shift-Mod-z": () => run(redo),
      "Mod-y": () => run(redo),
    };
  },
});

const normalizeQuestionList = (questions = []) => {
  const rawQuestions = Array.isArray(questions) ? questions : [];
  const normalized = rawQuestions.flatMap((entry) => {
    const question = typeof entry === "string" ? entry : "";
    return question.split(/\r?\n/);
  });

  return normalized.length ? normalized : [""];
};

const extractText = (node) => {
  if (!node) return "";
  if (node.type === "text") return node.text || "";
  const children = Array.isArray(node.content) ? node.content : [];
  return children.map((child) => extractText(child)).join("");
};

const extractQuestionsFromDoc = (doc) => {
  const bulletList = Array.isArray(doc?.content)
    ? doc.content.find((node) => node?.type === "bulletList")
    : null;
  const listItems = Array.isArray(bulletList?.content) ? bulletList.content : [];

  if (!listItems.length) return [""];

  return normalizeQuestionList(
    listItems.map((item) => {
      const paragraph = Array.isArray(item?.content)
        ? item.content.find((node) => node?.type === "paragraph")
        : null;
      return extractText(paragraph);
    })
  );
};

const createDocFromQuestions = (questions = []) => ({
  type: "doc",
  content: [
    {
      type: "bulletList",
      content: normalizeQuestionList(questions).map((question) => {
        const content = question
          ? [
            {
              type: "text",
              text: question,
            },
          ]
          : [];

        return {
          type: "listItem",
          content: [
            {
              type: "paragraph",
              content,
            },
          ],
        };
      }),
    },
  ],
});

const areQuestionListsEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  return left.every((item, index) => item === right[index]);
};

const getCurrentListItemIndex = (state) => {
  const { $from } = state.selection;

  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type === state.schema.nodes.listItem) {
      return $from.index(depth - 1);
    }
  }

  return -1;
};

const StrictListPasteHandler = Extension.create({
  name: "strictListPasteHandler",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handlePaste: (view, event) => {
            const text = event?.clipboardData?.getData("text/plain") || "";
            if (!text.includes("\n")) return false;

            const pastedLines = text
              .split(/\r?\n/)
              .map((line) => line.trim())
              .filter(Boolean);

            // Multi-line paste with only blank lines should do nothing.
            if (!pastedLines.length) return true;

            const currentQuestions = extractQuestionsFromDoc(this.editor.getJSON());
            const currentIndex = getCurrentListItemIndex(view.state);
            const safeIndex =
              currentIndex >= 0 ? Math.min(currentIndex, currentQuestions.length - 1) : -1;
            const nextQuestions = [...currentQuestions];

            if (safeIndex < 0) {
              nextQuestions.push(...pastedLines);
            } else if ((nextQuestions[safeIndex] || "").trim() === "") {
              nextQuestions.splice(safeIndex, 1, ...pastedLines);
            } else {
              nextQuestions.splice(safeIndex + 1, 0, ...pastedLines);
            }

            this.editor.commands.setContent(createDocFromQuestions(nextQuestions));
            this.editor.commands.focus("end");
            return true;
          },
        },
      }),
    ];
  },
});

const StrictListItem = ListItem.extend({
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const didSplit = this.editor.commands.splitListItem(this.name);

        if (didSplit) {
          return true;
        }

        return this.editor.commands.command(({ state, dispatch }) => {
          const { selection } = state;
          const { $from } = selection;

          for (let depth = $from.depth; depth > 0; depth -= 1) {
            if ($from.node(depth).type !== state.schema.nodes.listItem) {
              continue;
            }

            const listItemNode = $from.node(depth);
            const insertPos = $from.before(depth) + listItemNode.nodeSize;
            const newListItem = state.schema.nodes.listItem.createAndFill();

            if (!newListItem) {
              return true;
            }

            const tr = state.tr.insert(insertPos, newListItem);
            const selectionPos = Math.min(insertPos + 2, tr.doc.content.size);
            tr.setSelection(TextSelection.near(tr.doc.resolve(selectionPos)));
            if (dispatch) {
              dispatch(tr.scrollIntoView());
            }
            return true;
          }

          return true;
        });
      },
      Backspace: () => {
        return this.editor.commands.command(({ state, dispatch }) => {
          const { selection, schema } = state;
          const { $from } = selection;

          if (!selection.empty) return false;
          if ($from.parent.type !== schema.nodes.paragraph) return false;
          if ($from.parentOffset !== 0 || $from.parent.content.size !== 0) return false;

          for (let depth = $from.depth; depth > 0; depth -= 1) {
            if ($from.node(depth).type !== schema.nodes.listItem) {
              continue;
            }

            const listDepth = depth - 1;
            const itemIndex = $from.index(listDepth);

            // Keep the first list item; prevent exiting strict list mode.
            if (itemIndex <= 0) {
              return true;
            }

            const listNode = $from.node(listDepth);
            const previousItem = listNode.child(itemIndex - 1);
            const currentItemFrom = $from.before(depth);
            const currentItemTo = $from.after(depth);
            const previousItemPos = currentItemFrom - previousItem.nodeSize;
            const cursorPos = Math.max(previousItemPos + 1, previousItemPos + previousItem.nodeSize - 2);
            const tr = state.tr.delete(currentItemFrom, currentItemTo);

            tr.setSelection(TextSelection.near(tr.doc.resolve(cursorPos), -1));
            if (dispatch) {
              dispatch(tr.scrollIntoView());
            }
            return true;
          }

          return false;
        });
      },
    };
  },
});

const StrictBulletListEditor = ({
  questions,
  onChange,
  editable = true,
  className,
  autoFocus = false,
  placeholder = "Write one question per line. Press Enter to add more questions.",
}) => {
  const normalizedQuestions = useMemo(
    () => normalizeQuestionList(questions),
    [questions]
  );

  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const editor = useEditor({
    extensions: [
      StrictListDocument,
      Paragraph,
      Text,
      BulletList,
      StrictListItem,
      StrictListPasteHandler,
      EditorHistory,
    ],
    content: createDocFromQuestions(normalizedQuestions),
    editable,
    autofocus: autoFocus,
    editorProps: {
      attributes: {
        class: "faq-question-list-editor-content",
      },
    },
    onUpdate: ({ editor: tiptapEditor }) => {
      const nextQuestions = extractQuestionsFromDoc(tiptapEditor.getJSON());
      if (!onChange) return;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        onChange(nextQuestions);
      }, 400);
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(Boolean(editable));
  }, [editor, editable]);

  useEffect(() => {
    if (!editor) return;

    const currentQuestions = extractQuestionsFromDoc(editor.getJSON());
    if (areQuestionListsEqual(currentQuestions, normalizedQuestions)) {
      return;
    }

    editor.commands.setContent(createDocFromQuestions(normalizedQuestions), false);
  }, [editor, normalizedQuestions]);

  const wrapperClassName = [
    "faq-question-list-editor",
    editable ? "is-editable" : "",
    editable ? "has-helper-text" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleWrapperMouseDown = (event) => {
    if (!editable || !editor) return;
    const target = event.target;
    if (target instanceof Element && target.closest(".faq-question-list-editor-content")) {
      return;
    }
    event.preventDefault();
    editor.chain().focus("end").run();
  };

  return (
    <div className={wrapperClassName} onMouseDown={handleWrapperMouseDown}>
      <EditorContent editor={editor} />
      {editable && placeholder ? (
        <div className="faq-question-list-editor-helper-text">{placeholder}</div>
      ) : null}
    </div>
  );
};

export default StrictBulletListEditor;
